import { Fragment, createElement, isValidElement, type ReactNode } from 'react';
import { EN } from './en';

/**
 * La lingua del pannello: italiano o inglese, per persona (blocco 31).
 *
 * Il codice resta in italiano, e il testo italiano è la chiave: `t('Collega
 * un repository')` rende la frase com'è in italiano e la cerca nel dizionario
 * inglese (`lib/en/`) altrimenti. Una frase senza traduzione esce in italiano
 * invece di sparire, e un test pretende che non ce ne siano.
 *
 * Le parti variabili si scrivono `{nome}`: `t('Installa su {n} istanze', { n })`.
 * Se una è un elemento React — un nome in grassetto, un link — `t` restituisce
 * un nodo invece di una stringa, e la frase resta una sola da tradurre invece
 * di tre pezzi che in inglese andrebbero in un altro ordine.
 *
 * Le costanti — etichette di stato, titoli dei job — restano in italiano e si
 * traducono dove si mostrano: calcolate una volta all'import, non saprebbero
 * che la lingua è cambiata.
 *
 * Una parola con due sensi in italiano e due in inglese porta il senso dopo
 * una barra: `t('Ferma|azione')` si legge «Ferma» e in inglese è «Stop»,
 * mentre `t('Ferma')`, lo stato, è «Stopped».
 */

export type Lingua = 'it' | 'en';

const CHIAVE = 'molo.lingua';

/** La lingua del browser: italiano se è italiano, inglese altrimenti. */
export function linguaDelBrowser(): Lingua {
  return (navigator.language || 'it').toLowerCase().startsWith('it') ? 'it' : 'en';
}

// Prima che `/me` risponda — la schermata d'avvio, il login — vale l'ultima
// scelta fatta su questo browser, o quella del browser stesso.
function iniziale(): Lingua {
  try {
    const s = localStorage.getItem(CHIAVE);
    if (s === 'it' || s === 'en') return s;
  } catch {
    // Archivio del browser chiuso: si va con la lingua del browser.
  }
  return linguaDelBrowser();
}

let corrente: Lingua = iniziale();
document.documentElement.lang = corrente;

export function lingua(): Lingua {
  return corrente;
}

/** Cambia la lingua per le prossime frasi, e la ricorda su questo browser. */
export function impostaLingua(l: Lingua) {
  corrente = l;
  document.documentElement.lang = l;
  try {
    localStorage.setItem(CHIAVE, l);
  } catch {
    // Non la ricorda: alla prossima apertura la dirà di nuovo `/me`.
  }
}

/** La lingua per `Intl` e `toLocale*`: date, ore, numeri. */
export function localeIntl(): string {
  return corrente === 'en' ? 'en-GB' : 'it-IT';
}

type Valori = Record<string, string | number>;
type ValoriNodo = Record<string, ReactNode>;

export function t(it: string, valori?: Valori): string;
export function t(it: string, valori: ValoriNodo): ReactNode;
export function t(chiave: string, valori?: ValoriNodo): ReactNode {
  const it = chiave.split('|')[0] ?? chiave;
  const modello = corrente === 'en' ? (EN[chiave] ?? it) : it;
  if (!valori) return modello;
  // Le posizioni dispari sono i nomi fra graffe.
  const pezzi = modello.split(/\{(\w+)\}/);
  const nodi = pezzi.map((p, i) => (i % 2 === 0 ? p : p in valori ? valori[p] : `{${p}}`));
  if (nodi.every((n) => typeof n === 'string' || typeof n === 'number')) return nodi.join('');
  return createElement(
    Fragment,
    null,
    ...nodi.map((n, i) => (isValidElement(n) ? createElement(Fragment, { key: i }, n) : n)),
  );
}
